import { Component, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from './shared/services/authentication.service';
import { TranslationService } from './shared/services/translation.service';
import { BrandingService } from './shared/services/branding.service';
import { MixpanelService } from './shared/services/mixpanel-service.service';
import { LocalStorageService } from './shared/services/local-storage.service';
import { productFruits } from 'product-fruits';
import { environment } from '../environments/environment';
import { AngularFaviconService } from 'angular-favicon';
import { PermissionService } from './shared/services/permission.service';
import { GetterSetterService } from './shared/services/getter-setter.service';
import { IdleSessionService } from './shared/services/idle-session.service';
import { applySafariLayoutFixClass } from './shared/utils/browser-platform';
import { CookieConsentService, TrackingScriptsService } from '@libs/cookie-consent';

/** The Site24x7 app key and ChurnZero script URL that used to sit unconditionally in index.html. */
const SITE24X7_APP_KEY = '440c1e9880e8b81dbac896f5f7a4cc45';
const CHURNZERO_SCRIPT_URL = 'https://anchanto.eu2app.churnzero.net/churnzero.js';
/** The ProductFruits workspace code used by this app. */
const PRODUCT_FRUITS_CODE = 'AgG3Zr5JG64s7rmL';

type ProductFruitsType = typeof productFruits;
declare global {
    interface Window {
        // productFruits?: ProductFruitsType;
    }
}
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, OnChanges {
    title = 'aryabhata';
    isDpdLogistic: boolean = false;
    //Empty Commit

    public brandingColorsObj: Subscription;
    public isAsendiaUKCustomer: any;
    public wmsEnabled: boolean = false;
    public isLoggedIn: boolean = false;

    private routerSubscription: Subscription;
    private consentSubscription: Subscription;
    private productFruitsInitialized: boolean = false;

    constructor(
        private router: Router,
        private authenticationService: AuthenticationService,
        private brandingService: BrandingService,
        private translate: TranslateService,
        public translationService: TranslationService,
        private mixpanelService: MixpanelService,
        private localStorageService: LocalStorageService,
        private ngxFavicon: AngularFaviconService,
        private permissionService: PermissionService,
        private getterSetter: GetterSetterService,
        private idleSessionService: IdleSessionService,
        private cookieConsentService: CookieConsentService,
        private trackingScriptsService: TrackingScriptsService
    ) {
        applySafariLayoutFixClass();
        this.translationService.initializeLanguageFromProfile();

        const language = this.translationService.getBrowsersLocals();
        this.translate.use(language);
        this.getterSetter.rtl = false;
        // language = 'ar';
        if (
            language !== 'ar' &&
            document.getElementsByTagName('html')[0].hasAttribute('dir')
        ) {
            document.getElementsByTagName('html')[0].removeAttribute('dir');
        } else if (
            language === 'ar' &&
            !document.getElementsByTagName('html')[0].hasAttribute('dir')
        ) {
            this.getterSetter.rtl = true;
            document
                .getElementsByTagName('html')[0]
                ?.setAttribute('dir', 'rtl');
        }
    }

    ngOnChanges() {
        this.checkWmsStatus();
    }

    checkWmsStatus() {
        const hasSession = this.authenticationService.hasActiveSession();
        const userData = this.localStorageService.get('userData');
        const currentUrl = this.router.url;

        // Case: login or forgot-password pages
        if (currentUrl?.includes('login') || currentUrl?.includes('forgot-password')) {
            this.isLoggedIn = false;
            this.wmsEnabled = true;
            return;
        }

        // Case: Fulfilment customer
        if (userData?.customer_type?.type_name === 'Non-Fulfilment') {
            this.wmsEnabled = true;
            return;
        }

        // Case: Non-Fulfilment customer
        this.isLoggedIn = hasSession;

        if (this.isLoggedIn && userData) {
            const wmsChannel = userData?.channel_permissions?.find(
                (channel: any) => channel?.channel_name === 'WMS'
            );

            this.wmsEnabled = !!(
                wmsChannel &&
                (wmsChannel?.integration_status === true ||
                    wmsChannel?.integration_status === 1)
            );
        } else {
            // When not logged in, ensure banner doesn't show
            this.isLoggedIn = false;
            this.wmsEnabled = true;
        }
    }

    ngOnInit() {
        this.mixpanelService.init();

        // Site24x7 and ChurnZero used to load unconditionally from
        // index.html. They now run only once the matching consent grant
        // exists, and re-check whenever consent changes, so a grant made
        // after bootstrap still takes effect.
        this.trackingScriptsService.loadSite24x7WhenPermitted(SITE24X7_APP_KEY);
        this.trackingScriptsService.loadChurnZeroWhenPermitted(CHURNZERO_SCRIPT_URL);

        // Remove the cleartext password persisted by earlier builds. Done here
        // as well as on the login page so that users with an active session are
        // cleaned up on their next page load, without waiting for a logout.
        this.localStorageService.removeItem('pass');

        if (this.localStorageService.checkSession()) {
            this.permissionService.initializePermissions();
        }
        this.idleSessionService.initialize();

        const storedLogoImage = this.localStorageService.get('LogoImage');
        if (storedLogoImage) {
            this.brandingService.applyBrandingFromLogoImageStorage(storedLogoImage);
        }

        this.isAsendiaUKCustomer = this.localStorageService.get('isAsendiaUserUk');
        if (this.isAsendiaUKCustomer || this.authenticationService.hasActiveSession()) {
            this.getBrandingColors();
        }
        // Monitor router events to detect login/logout
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.translationService.initializeLanguageFromProfile();
                // Always check WMS status after navigation to ensure banner state is correct
                setTimeout(() => this.checkWmsStatus(), 0);

                // Re-initialize Product Fruits if user data becomes available after login
                if (environment.environmentName === 'Prod') {
                    this.loadPf();
                }
            });

        this.checkWmsStatus();

        if (environment.environmentName === 'Prod') {
            this.loadPf();
        }
        // A grant made after bootstrap (the consent modal, account settings)
        // must be able to start Product Fruits without a reload.
        this.consentSubscription = this.cookieConsentService.consent$.subscribe(
            () => this.loadPf()
        );
    }

    setFavicon(data) {
        let iconUrl: string;

        if (data?.fav_image) {
            iconUrl = data?.fav_image;
        } else {
            iconUrl = './assets/images/oxm.png';
        }

        this.ngxFavicon.setFavicon(iconUrl);
    }

    getBrandingColors() {
        // const param =
        //     this.router['location']?._platformLocation?.location?.origin ||
        //     this.router['location']?._locationStrategy?._platformLocation
        //         ?.location?.origin;
        const param = window?.location?.origin;
        this.brandingColorsObj = this.authenticationService
            .getLogoImage(param)
            .subscribe((response: any) => {
                this.setFavicon(response.data);
                this.localStorageService.set('LogoImage', response.data);
                this.brandingService.applyBrandingFromLogoImageStorage(response.data);
                if (response.result == 100) {
                    this.brandingService.setBrandingValues(
                        response.data.preference_setting
                    );
                }
            });
    }

    loadPf() {
        // ProductFruits' own package injects nothing until init() is called,
        // so withholding that call is the whole consent gate for it — same
        // technique as the security review applied to WMS.
        if (environment.environmentName !== 'Prod') return;
        if (!this.cookieConsentService.isAllowed('productfruits')) return;

        const userData = this.localStorageService?.get('userData');

        // Only initialize Product Fruits if user is logged in and has username
        if (!userData || !userData?.user_name) {
            // Reset flag if user logged out
            if (this.productFruitsInitialized) {
                this.productFruitsInitialized = false;
            }
            return;
        }

        // Skip if already initialized with same user
        if (this.productFruitsInitialized) {
            return;
        }

        const currentLanguage = userData?.language;
        const email = userData?.email;
        const tempUsername = userData?.user_name;
        const firstName = userData?.first_name;
        const lastName = userData?.last_name;

        productFruits.init(
            PRODUCT_FRUITS_CODE,
            currentLanguage?.code || 'en',
            {
                email: email || '',
                username: tempUsername, // Required field - now guaranteed to exist
                firstname: firstName || '',
                lastname: lastName || ''
            },
            { disableLocationChangeDetection: false }
        );
        productFruits.safeExec(($pf: any) => {
            // console.log('Product Fruits initialized:', {
            //     username: tempUsername,
            //     email: email,
            //     language: currentLanguage?.code || 'en',
            //     currentUrl: window.location.href,
            //     pfInstance: $pf
            // });

            // Check if Product Fruits is ready
            if ($pf && typeof $pf === 'object' && !Array.isArray($pf)) {
                // console.log('Product Fruits API available:', {
                //     isReady: typeof $pf.isReady === 'function' ? $pf.isReady() : 'N/A',
                //     currentUser: typeof $pf.getCurrentUser === 'function' ? $pf.getCurrentUser() : 'N/A'
                // });
            }
        });

        this.productFruitsInitialized = true;
    }

    ngOnDestroy() {
        if (this.brandingColorsObj) {
            this.brandingColorsObj.unsubscribe();
        }
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
        if (this.consentSubscription) {
            this.consentSubscription.unsubscribe();
        }
        this.idleSessionService.ngOnDestroy();
    }
}
